import type { ListerData } from "./types";
import type { RegaValidateResponse } from "@/types/rega";
import { getRegaDisplayPrice, getRegaEffectiveListingType } from "@/lib/regaPricing";

export const LICENSE_NUMBER_LENGTH = 10;

/**
 * Listing type + price the wizard publishes for a REGA-validated ad:
 * type from REGA's advertisementType (default rent when absent), price as the
 * derived total — sale total for sale ads, ANNUAL rent for rent ads (never the
 * raw per-m² rate, and never the sale total for a rent ad — review #3309).
 */
export function deriveListingPricing(
  regaData: RegaValidateResponse | null | undefined,
): { listingType: "rent" | "sale"; price: number } {
  const listingType = getRegaEffectiveListingType(regaData, "rent");
  return { listingType, price: getRegaDisplayPrice(regaData, listingType) };
}

export function stripNonDigits(value: string): string {
  return value.replace(/\D/g, "");
}

export function isLicenseLengthInvalid(value: string): boolean {
  return value.length > 0 && value.length !== LICENSE_NUMBER_LENGTH;
}

export function isValidateLicenseButtonEnabled(
  idType: string,
  licenseNumber: string,
  idNumber: string,
  isPending: boolean,
): boolean {
  return !!(
    idType &&
    licenseNumber.length === LICENSE_NUMBER_LENGTH &&
    idNumber &&
    !isPending
  );
}

export function applyListerChange(
  prev: ListerData,
  patch: Partial<ListerData>,
  storedUnifiedNumber: string | null | undefined,
): ListerData {
  if (patch.idType === "corporate" && patch.idType !== prev.idType) {
    return { ...prev, ...patch, idNumber: storedUnifiedNumber ?? "" };
  }
  return { ...prev, ...patch };
}

/**
 * True only when the corporate unified number is worth persisting — which
 * includes having a company to persist it to. Typed as a predicate on
 * `companyId` so callers get that narrowing for free instead of asserting it.
 */
export function shouldPersistUnifiedNumber(
  idType: string,
  idNumber: string,
  storedUnifiedNumber: string | null | undefined,
  companyId: string | null,
): companyId is string {
  return !!(
    idType === "corporate" &&
    idNumber &&
    idNumber !== storedUnifiedNumber &&
    companyId
  );
}
